import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { LinkNode, AutoLinkNode } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useEffect } from 'react';

import './styles.css';

interface LexicalRendererProps {
  content: string;
  className?: string;
}

// Plugin to set content and make it read-only
function ReadOnlyContentPlugin({ content }: { content: string }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    if (content) {
      try {
        const editorState = editor.parseEditorState(content);
        editor.setEditorState(editorState);
        editor.setEditable(false);
      } catch (error) {
        console.error('Error rendering content:', error);
      }
    }
  }, [editor, content]);

  return null;
}

export function LexicalRenderer({ content, className = '' }: LexicalRendererProps) {
  const initialConfig = {
    namespace: 'AnnouncementRenderer',
    theme: {
      paragraph: 'lexical-paragraph',
      text: {
        bold: 'lexical-bold',
        italic: 'lexical-italic',
        underline: 'lexical-underline',
      },
      link: 'lexical-link',
    },
    editable: false,
    onError: (error: Error) => {
      console.error('Lexical renderer error:', error);
    },
    nodes: [LinkNode, AutoLinkNode],
  };

  return (
    <LexicalComposer initialConfig={initialConfig}>
      <div className={`lexical-renderer ${className}`}>
        <RichTextPlugin
          contentEditable={<ContentEditable className="lexical-content-editable-readonly" />}
          placeholder={null}
          ErrorBoundary={LexicalErrorBoundary}
        />
        <ReadOnlyContentPlugin content={content} />
      </div>
    </LexicalComposer>
  );
}
